import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_URL, apiFetch } from '../api/client';
import { SITE_INITIALS, SITE_NAME, SITE_TAGLINE } from '../config/branding';

function completeLogin(data, username, onLoggedIn, navigate, redirectTo) {
  const loggedInUser = data.username || username;
  onLoggedIn({
    authenticated: true,
    username: loggedInUser,
    id: data.id,
    roles: data.roles || [],
    role_labels: data.role_labels || [],
    permissions: data.permissions || [],
    totp_enabled: Boolean(data.totp_enabled),
  });
  const dest =
    redirectTo && redirectTo !== '/' && !redirectTo.startsWith('/login')
      ? redirectTo
      : '/dashboard';
  navigate(dest, { replace: true });
}

export function LoginPage({ onLoggedIn, redirectTo = '/dashboard' }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [step, setStep] = useState('password');
  const [pendingUsername, setPendingUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || data.message || 'Giriş başarısız.');
        return;
      }
      if (data.requires_2fa) {
        setPendingUsername(data.username || username.trim());
        setStep('2fa');
        setTotpCode('');
        setPassword('');
        return;
      }
      completeLogin(data, username, onLoggedIn, navigate, redirectTo);
    } catch (err) {
      setError(err.message || 'Bağlantı hatası.');
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: totpCode.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || data.message || 'Doğrulama kodu hatalı.');
        return;
      }
      completeLogin(data, pendingUsername, onLoggedIn, navigate, redirectTo);
    } catch (err) {
      setError(err.message || 'Bağlantı hatası.');
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = () => {
    setStep('password');
    setTotpCode('');
    setError('');
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-brand">
          <div className="logo-mark logo-mark--lg">{SITE_INITIALS}</div>
          <h1 className="login-title">{SITE_NAME}</h1>
          <p className="login-subtitle">{SITE_TAGLINE}</p>
        </div>

        {step === 'password' ? (
          <form className="login-form" onSubmit={submitPassword}>
            <div className="form-group">
              <label className="form-label" htmlFor="login-username">
                Kullanıcı adı
              </label>
              <input
                id="login-username"
                className="form-input"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-password">
                Şifre
              </label>
              <input
                id="login-password"
                className="form-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error ? (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            ) : null}

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? 'Giriş yapılıyor…' : 'Giriş yap'}
            </button>
          </form>
        ) : (
          <form className="login-form" onSubmit={submitTotp}>
            <p className="text-muted text-sm" style={{ marginBottom: '14px', lineHeight: 1.5 }}>
              <strong>{pendingUsername}</strong> için authenticator uygulamasındaki 6 haneli kodu girin.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="login-totp">
                Doğrulama kodu
              </label>
              <input
                id="login-totp"
                className="form-input login-totp-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="000000"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
              />
            </div>

            {error ? (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            ) : null}

            <button type="submit" className="btn btn-primary btn-block" disabled={loading || totpCode.length < 6}>
              {loading ? 'Doğrulanıyor…' : 'Doğrula ve giriş yap'}
            </button>
            <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: '8px' }} onClick={backToPassword}>
              Geri dön
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
